import { type Email } from '@/types/EmailMessage'
import { createEmail } from '@/factories/emailFactory'

export const EmailModel = {
  create(data: Partial<Email>): Email {
    return createEmail(data)
  },

  validate(email: Email): boolean {
    if (!email.to || (Array.isArray(email.to) && email.to.length === 0)) {
      console.log('Email validation failed: missing or empty "to" field', email)
      return false
    }
    if (!email.from) {
      console.log('Email validation failed: missing "from" field', email)
      return false
    }
    if (!email.message?.subject) {
      console.log('Email validation failed: missing subject', email)
      return false
    }
    if (!email.message?.html && !email.message?.text && !email.template) {
      console.log('Email validation failed: missing content (html, text) and template', email)
      return false
    }
    return true
  },

  formatRecipient(to: string | string[] | undefined): string {
    if (!to) return 'N/A'
    return Array.isArray(to) ? to.join(', ') : to
  },

  formatDate(date: Date | undefined): string {
    if (!date) return 'N/A'
    return new Date(date).toLocaleString()
  },

  formatDisplayName(email: Email): string {
    return `${email.message?.subject || 'No Subject'} (${this.formatRecipient(email.to)})`
  },

  isDeliverySuccessful(email: Email): boolean {
    return !!email.delivery && email.delivery.state === 'SUCCESS'
  },

  hasDeliveryFailed(email: Email): boolean {
    return !!email.delivery && email.delivery.state === 'ERROR'
  },

  isBeingProcessed(email: Email): boolean {
    return (
      !!email.delivery &&
      (email.delivery.state === 'PENDING' || email.delivery.state === 'PROCESSING')
    )
  },

  getDeliveryStatusSummary(email: Email) {
    if (!email.delivery) {
      return {
        status: 'Not Processed',
        statusClass: 'info',
      }
    }

    switch (email.delivery.state) {
      case 'SUCCESS':
        return {
          status: 'Delivered',
          statusClass: 'success',
          deliveredTo: email.delivery.info?.accepted,
          messageId: email.delivery.info?.messageId,
        }
      case 'ERROR':
        return {
          status: 'Failed',
          statusClass: 'danger',
          errorMessage: email.delivery.error || 'Unknown error',
        }
      case 'PROCESSING':
        return {
          status: 'Processing',
          statusClass: 'warning',
        }
      case 'PENDING':
        return {
          status: 'Pending',
          statusClass: 'info',
        }
      case 'RETRY':
        return {
          status: 'Retry Scheduled',
          statusClass: 'warning',
        }
      default:
        return {
          status: 'Unknown',
          statusClass: 'secondary',
        }
    }
  },

  getDeliverySummary(email: Email): string {
    const { delivery } = email

    if (!delivery) return 'Not processed'

    if (delivery.state === 'SUCCESS') {
      const acceptedCount = delivery.info?.accepted?.length || 0
      return `Successfully delivered to ${acceptedCount} recipient${acceptedCount !== 1 ? 's' : ''}`
    }

    if (delivery.state === 'ERROR') {
      return `Delivery failed: ${delivery.error || 'Unknown error'}`
    }

    if (delivery.state === 'PROCESSING') {
      return 'Email is currently being processed'
    }

    if (delivery.state === 'PENDING') {
      return 'Email is pending delivery'
    }

    if (delivery.state === 'RETRY') {
      return `Scheduled for retry (${delivery.attempts} previous attempts)`
    }

    return 'Unknown delivery status'
  },

  getDeliveryTime(email: Email): number | null {
    if (!email.delivery?.startTime || !email.delivery?.endTime) return null
    const start = new Date(email.delivery.startTime).getTime()
    const end = new Date(email.delivery.endTime).getTime()
    return Math.round((end - start) / 1000)
  },
}
